import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, TransportCardLayout } from './common/DetailCardAndPinLayout'
import ItemTitle from './common/ItemTitle'
import Attachments from './common/Attachments'
import type { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { useAirport } from 'tripPlanner/hooks/api/flights'
import type { FlightItem } from 'tripPlanner/types/tripItem'
import { formatShortDateRange } from 'tripPlanner/utils'
import { formatPipeSeparator } from 'LuxKit/intl/separators'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import FlightSearchPriceAndCTAs from './PriceAndCTAs/FlightSearchPriceAndCTAs'
import styled from 'styled-components'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'

const DetailsBlock = styled(BodyTextBlock)`
  clear: both;
`

function getAirportName(
  airport: App.AirportLocation | undefined,
  placeName: string,
) {
  let airportName: string | undefined

  if (airport) {
    airportName =
      airport.airportName || airport.cityAirportName || airport.cityDesc
  }
  // Fallback if no airport or if it's missing data
  if (!airportName) {
    airportName = placeName
  }
  return airportName
}

interface Props {
  item: FlightItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailFlightSearchCard({ item, variant }: Props) {
  const intl = useIntl()
  const { fareTypeLabelMap, fareCabinLabelMap } = useFlightFareLabels()
  const [inFocus, setInFocus] = useState(false)
  const [showComment, setShowComment] = useState<boolean>(!!item.notes)

  const { data: trip } = useTrip({ tripId: item.tripId })

  const {
    originAirportCode,
    destinationAirportCode,
    savedItemData: {
      fareClass,
      fareType,
      travellerCounts,
    } = {},
  } = item

  const { data: departureAirport, isLoading: isDepartureAirportLoading } =
    useAirport({
      code: originAirportCode,
    })

  const { data: arrivalAirport, isLoading: isArrivalAirportLoading } =
    useAirport({
      code: destinationAirportCode,
    })

  const departureAirportName = getAirportName(
    departureAirport,
    item.startPlace.name,
  )
  const arrivalAirportName = getAirportName(arrivalAirport, item.endPlace.name)
  const hasAirportNames = !!(departureAirportName && arrivalAirportName)
  const isLoadingAirportData =
    isDepartureAirportLoading || isArrivalAirportLoading

  const title = hasAirportNames ? (
    <FormattedMessage
      id="EwH4AzrKSgyGVN4Gvyw8"
      defaultMessage="{departure} {icon} {arrival}"
      description="Flight route showing departure and arrival airport names with an arrow icon between them"
      values={{
        departure: departureAirportName,
        icon: <LineArrowRightIcon size="S" />,
        arrival: arrivalAirportName,
      }}
    />
  ) : isLoadingAirportData ? (
    <TextLoadingBox width={160} />
  ) : (
    intl.formatMessage({
      id: '3CdLx4oyi5PAk9aWHw_0',
      defaultMessage: 'Flight',
      description: 'Fallback title for a flight search card when airport names are unavailable',
    })
  )

  const fareClassLabel = fareClass ? fareCabinLabelMap[fareClass as keyof typeof fareCabinLabelMap] : undefined
  const fareTypeLabel = fareType ? fareTypeLabelMap[fareType] : undefined

  const travellerCount: App.Occupants = travellerCounts ?? {
    adults: 0,
  }

  const details = [
    formatShortDateRange(item),
    fareClassLabel,
    fareTypeLabel,
    formatOccupantsShort(travellerCount),
  ]
    .filter(Boolean)
    .join(formatPipeSeparator(intl))

  const { itemNumber, itemColor } = useMemo(
    () => getItemPinBadge(item, variant, trip),
    [item, variant, trip],
  )

  const onFocus = useCallback(() => {
    setInFocus(true)
  }, [])

  const onBlur = useCallback(() => {
    setInFocus(false)
  }, [])

  const onRequestAddComment = useCallback(() => {
    setShowComment(true)
    setInFocus(true)
  }, [])

  const onNotesChange = useCallback((notes: string | undefined) => {
    setShowComment(!!notes)
  }, [])

  return (
    <DetailCardAndPinLayout data-testid="detail-card-flight-search">
      <DetailCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <TransportCardLayout>
        <TransportCardLayout.Image>
          <HoverOnlyRegenerateItemButton item={item} />
        </TransportCardLayout.Image>
        <TransportCardLayout.Content>
          <CornerControls item={item} onRequestAddComment={onRequestAddComment} />
          <VerticalSpacer gap={8}>
            <ItemTitle>{title}</ItemTitle>
            {details && (
              <DetailsBlock variant="small" colour="neutral-two">
                {details}
              </DetailsBlock>
            )}
            <Attachments item={item} />
          </VerticalSpacer>
        </TransportCardLayout.Content>
        <TransportCardLayout.Timeline>
          <FlightSearchPriceAndCTAs
            item={item}
            buttonKind={variant === 'drawer' ? 'primary' : 'secondary'}
          />
        </TransportCardLayout.Timeline>
        {showComment && (
          <TransportCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={onFocus}
              onBlur={onBlur}
              onNotesChange={onNotesChange}
            />
          </TransportCardLayout.Comment>
        )}
      </TransportCardLayout>
    </DetailCardAndPinLayout>
  )
}

export default DetailFlightSearchCard
